import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Edit2, Save, Plus, Trash2 } from "lucide-react";

export default function AdminPricing() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newPlan, setNewPlan] = useState({ name: "", product_limit: 100, prices: { monthly: 0, weekly: 0, daily: 0 } });

  const fetchPlans = () => {
    api.get("/admin/pricing-matrix").then((r) => setPlans(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleSave = async () => {
    try {
      await api.put(`/admin/pricing-matrix/${editing.id}`, {
        name: editing.name,
        prices: {
          monthly: parseFloat(editing.prices.monthly),
          weekly: parseFloat(editing.prices.weekly),
          daily: parseFloat(editing.prices.daily),
        },
        product_limit: parseInt(editing.product_limit),
      });
      toast.success("Pakket bijgewerkt");
      setEditing(null);
      fetchPlans();
    } catch { toast.error("Fout bij opslaan"); }
  };

  const handleCreate = async () => {
    try {
      await api.post("/admin/pricing-matrix", {
        name: newPlan.name,
        product_limit: parseInt(newPlan.product_limit),
        prices: {
          monthly: parseFloat(newPlan.prices.monthly),
          weekly: parseFloat(newPlan.prices.weekly),
          daily: parseFloat(newPlan.prices.daily),
        },
      });
      toast.success("Nieuw pakket aangemaakt");
      setShowAdd(false);
      setNewPlan({ name: "", product_limit: 100, prices: { monthly: 0, weekly: 0, daily: 0 } });
      fetchPlans();
    } catch (err) { toast.error(err.response?.data?.detail || "Fout bij aanmaken"); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`${t("admin.delete_plan_confirm")} "${name}"`)) return;
    try {
      await api.delete(`/admin/pricing-matrix/${id}`);
      toast.success("Pakket verwijderd");
      fetchPlans();
    } catch { toast.error("Fout bij verwijderen"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="admin-pricing-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("admin.pricing")}</h1>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="add-plan-btn">
              <Plus className="w-4 h-4 mr-2" />{t("admin.add_plan")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("admin.new_plan")}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>{t("admin.plan_name")} *</Label>
                <Input value={newPlan.name} onChange={(e) => setNewPlan({...newPlan, name: e.target.value})} placeholder="Bijv. Extra Large" className="mt-1" data-testid="new-plan-name" />
              </div>
              <div>
                <Label>{t("admin.product_limit")}</Label>
                <Input type="number" value={newPlan.product_limit} onChange={(e) => setNewPlan({...newPlan, product_limit: e.target.value})} className="mt-1" data-testid="new-plan-limit" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>{t("admin.price_monthly")}</Label><Input type="number" step="0.01" value={newPlan.prices.monthly} onChange={(e) => setNewPlan({...newPlan, prices: {...newPlan.prices, monthly: e.target.value}})} className="mt-1" /></div>
                <div><Label>{t("admin.price_weekly")}</Label><Input type="number" step="0.01" value={newPlan.prices.weekly} onChange={(e) => setNewPlan({...newPlan, prices: {...newPlan.prices, weekly: e.target.value}})} className="mt-1" /></div>
                <div><Label>{t("admin.price_daily")}</Label><Input type="number" step="0.01" value={newPlan.prices.daily} onChange={(e) => setNewPlan({...newPlan, prices: {...newPlan.prices, daily: e.target.value}})} className="mt-1" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>{t("admin.cancel")}</Button>
              <Button onClick={handleCreate} className="bg-blue-600" disabled={!newPlan.name.trim()} data-testid="create-plan-btn">{t("admin.add")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead className="text-right">Producten</TableHead>
                <TableHead className="text-right">Maandelijks</TableHead>
                <TableHead className="text-right">Wekelijks</TableHead>
                <TableHead className="text-right">Dagelijks</TableHead>
                <TableHead className="text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.map((p) => (
                <TableRow key={p.id} className="table-row-hover" data-testid={`pricing-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-right font-mono-data">{p.product_limit?.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono-data">{p.prices?.monthly} EUR</TableCell>
                  <TableCell className="text-right font-mono-data">{p.prices?.weekly} EUR</TableCell>
                  <TableCell className="text-right font-mono-data">{p.prices?.daily} EUR</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing({ ...p })} data-testid={`edit-pricing-${p.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(p.id, p.name)} data-testid={`delete-pricing-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("admin.edit_plan")}: {editing.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>{t("admin.plan_name")}</Label>
                <Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} className="mt-1" data-testid="edit-plan-name" />
              </div>
              <div>
                <Label>{t("admin.product_limit")}</Label>
                <Input type="number" value={editing.product_limit} onChange={(e) => setEditing({...editing, product_limit: e.target.value})} className="mt-1" data-testid="edit-product-limit" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Maandelijks (EUR)</Label>
                  <Input type="number" step="0.01" value={editing.prices?.monthly} onChange={(e) => setEditing({...editing, prices: {...editing.prices, monthly: e.target.value}})} className="mt-1" data-testid="edit-price-monthly" />
                </div>
                <div>
                  <Label>Wekelijks (EUR)</Label>
                  <Input type="number" step="0.01" value={editing.prices?.weekly} onChange={(e) => setEditing({...editing, prices: {...editing.prices, weekly: e.target.value}})} className="mt-1" data-testid="edit-price-weekly" />
                </div>
                <div>
                  <Label>Dagelijks (EUR)</Label>
                  <Input type="number" step="0.01" value={editing.prices?.daily} onChange={(e) => setEditing({...editing, prices: {...editing.prices, daily: e.target.value}})} className="mt-1" data-testid="edit-price-daily" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>{t("admin.cancel")}</Button>
              <Button onClick={handleSave} className="bg-blue-600" data-testid="save-pricing-btn"><Save className="w-4 h-4 mr-2" />{t("admin.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
